import React from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Spinner } from '../../../shared/components';
import { formatDateTime } from '../../../shared/utils/localization';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionScopes } from '../../auth/permissions';
import {
  listConsultationTemplates,
  listConsultationTypes,
  listConsultationsPage,
} from '../services/consultationService';
import { formatConsultationAuditEntry } from '../consultationUtils';
import type { Consultation, ConsultationTemplate } from '../types/Consultation';
import type { AppointmentType } from '../../calendar/types/Calendar';
import styles from './ConsultationsPage.module.css';

type ConsultationStatusFilter = 'all' | 'withStatus' | 'withoutStatus';
type ConsultationTemplateFilter = 'all' | 'none' | string;
type ConsultationSortMode = 'updatedDesc' | 'updatedAsc' | 'titleAsc';

const VALID_SORT_MODES = new Set<ConsultationSortMode>(['updatedDesc', 'updatedAsc', 'titleAsc']);
const VALID_STATUS_FILTERS = new Set<ConsultationStatusFilter>(['all', 'withStatus', 'withoutStatus']);

export const ConsultationsPage: React.FC = () => {
  const { t } = useTranslation(['consultations', 'calendar', 'common']);
  const { hasPermission } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const canManageConsultations = hasPermission(PermissionScopes.consultationsManage);
  const [loading, setLoading] = React.useState(true);
  const [consultations, setConsultations] = React.useState<Consultation[]>([]);
  const [availableTemplates, setAvailableTemplates] = React.useState<ConsultationTemplate[]>([]);
  const [availableTypes, setAvailableTypes] = React.useState<AppointmentType[]>([]);
  const [totalResults, setTotalResults] = React.useState(0);
  const [hasNextPage, setHasNextPage] = React.useState(false);
  const [searchDraft, setSearchDraft] = React.useState(() => searchParams.get('q') ?? '');
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const [selectedConsultationId, setSelectedConsultationId] = React.useState<string | null>(null);
  const filterMenuRef = React.useRef<HTMLDivElement | null>(null);

  const includeDeleted = searchParams.get('deleted') === '1';
  const searchText = searchParams.get('q') ?? '';
  const statusFilter = VALID_STATUS_FILTERS.has((searchParams.get('status') as ConsultationStatusFilter) ?? 'all')
    ? ((searchParams.get('status') as ConsultationStatusFilter) ?? 'all')
    : 'all';
  const templateFilter = (searchParams.get('template') ?? 'all') as ConsultationTemplateFilter;
  const typeFilter = searchParams.get('type') ?? 'all';
  const sortMode = VALID_SORT_MODES.has((searchParams.get('sort') as ConsultationSortMode) ?? 'updatedDesc')
    ? ((searchParams.get('sort') as ConsultationSortMode) ?? 'updatedDesc')
    : 'updatedDesc';
  const page = Math.max(1, Number(searchParams.get('page') ?? '1') || 1);
  const pageSize = 25;
  const hasActiveFilters = includeDeleted || statusFilter !== 'all' || templateFilter !== 'all' || typeFilter !== 'all' || sortMode !== 'updatedDesc';
  const hasActiveSearch = Boolean(searchText.trim());
  const activeFilterCount = [
    includeDeleted,
    statusFilter !== 'all',
    templateFilter !== 'all',
    typeFilter !== 'all',
    sortMode !== 'updatedDesc',
  ].filter(Boolean).length;
  const showInitialLoading = loading && consultations.length === 0;

  React.useEffect(() => {
    if (!filtersOpen) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!filterMenuRef.current?.contains(event.target as Node)) {
        setFiltersOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [filtersOpen]);

  React.useEffect(() => {
    setSearchDraft(searchText);
  }, [searchText]);

  const updateQueryParam = React.useCallback((key: string, value?: string) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === 'all') {
      next.delete(key);
    } else {
      next.set(key, value);
    }

    next.delete('page');

    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleClearFilters = React.useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  const handleClearSearch = React.useCallback(() => {
    setSearchDraft('');
    updateQueryParam('q');
  }, [updateQueryParam]);

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      const normalizedDraft = searchDraft.trim();
      const normalizedQuery = searchText.trim();

      if (normalizedDraft === normalizedQuery) {
        return;
      }

      const next = new URLSearchParams(searchParams);
      if (normalizedDraft) {
        next.set('q', normalizedDraft);
      } else {
        next.delete('q');
      }
      next.delete('page');

      setSearchParams(next, { replace: true });
    }, 250);

    return () => {
      window.clearTimeout(timer);
    };
  }, [searchDraft, searchParams, searchText, setSearchParams]);

  React.useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const result = await listConsultationsPage({
          includeDeleted,
          templateId: templateFilter !== 'all' && templateFilter !== 'none' ? templateFilter : undefined,
          hasTemplate: templateFilter === 'none' ? false : undefined,
          appointmentTypeId: typeFilter !== 'all' && typeFilter !== 'none' ? typeFilter : undefined,
          hasAppointmentType: typeFilter === 'none' ? false : undefined,
          hasStatus: statusFilter === 'all' ? undefined : statusFilter === 'withStatus',
          search: searchText.trim() || undefined,
          sort: sortMode,
          page,
          pageSize,
        });
        if (cancelled) return;

        setConsultations(result.items);
        setTotalResults(result.total);
        setHasNextPage(result.hasNext);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [includeDeleted, page, pageSize, searchText, sortMode, statusFilter, templateFilter, typeFilter]);

  React.useEffect(() => {
    let cancelled = false;

    const loadFilterMetadata = async () => {
      const [templates, types] = await Promise.all([
        listConsultationTemplates(),
        listConsultationTypes(),
      ]);

      if (cancelled) {
        return;
      }

      setAvailableTemplates(templates);
      setAvailableTypes(types);
    };

    void loadFilterMetadata();

    return () => {
      cancelled = true;
    };
  }, []);

  const getConsultationTitle = React.useCallback((consultation: Consultation) => {
    return consultation.assetName
      || consultation.customerContactName
      || consultation.contactName
      || consultation.templateName
      || consultation.appointmentTypeName
      || consultation.subjectType
      || t('consultation', { defaultValue: 'Consultation' });
  }, [t]);

  const templateOptions = React.useMemo(() => {
    return availableTemplates
      .map((template) => ({ id: template.id, name: template.name }))
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [availableTemplates]);

  const typeOptions = React.useMemo(() => {
    return availableTypes
      .map((type) => ({ id: type.id, name: type.name }))
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [availableTypes]);

  const filteredConsultations = consultations;
  const selectedConsultation = React.useMemo(
    () => filteredConsultations.find((item) => item.id === selectedConsultationId) ?? filteredConsultations[0] ?? null,
    [filteredConsultations, selectedConsultationId],
  );

  React.useEffect(() => {
    if (filteredConsultations.length === 0) {
      setSelectedConsultationId(null);
      return;
    }

    if (!selectedConsultationId || !filteredConsultations.some((item) => item.id === selectedConsultationId)) {
      setSelectedConsultationId(filteredConsultations[0].id);
    }
  }, [filteredConsultations, selectedConsultationId]);

  const filterChips = React.useMemo(() => {
    const chips: Array<{ key: string; label: string; onRemove: () => void }> = [];

    if (statusFilter !== 'all') {
      chips.push({
        key: 'status',
        label: statusFilter === 'withStatus'
          ? t('statusSet', { defaultValue: 'Has status' })
          : t('statusUnset', { defaultValue: 'No status' }),
        onRemove: () => updateQueryParam('status'),
      });
    }

    if (templateFilter !== 'all') {
      chips.push({
        key: 'template',
        label: templateFilter === 'none'
          ? t('withoutTemplate', { defaultValue: 'Without template' })
          : templateOptions.find((option) => option.id === templateFilter)?.name || templateFilter,
        onRemove: () => updateQueryParam('template'),
      });
    }

    if (typeFilter !== 'all') {
      chips.push({
        key: 'type',
        label: typeFilter === 'none'
          ? t('withoutConsultationType', { defaultValue: 'Without consultation type' })
          : typeOptions.find((option) => option.id === typeFilter)?.name || typeFilter,
        onRemove: () => updateQueryParam('type'),
      });
    }

    if (sortMode !== 'updatedDesc') {
      chips.push({
        key: 'sort',
        label: sortMode === 'updatedAsc'
          ? t('oldestUpdated', { defaultValue: 'Oldest updated' })
          : t('titleAscending', { defaultValue: 'Title (A-Z)' }),
        onRemove: () => updateQueryParam('sort'),
      });
    }

    if (includeDeleted) {
      chips.push({
        key: 'deleted',
        label: t('includeDeleted', { ns: 'common', defaultValue: 'Include deleted' }),
        onRemove: () => updateQueryParam('deleted'),
      });
    }

    return chips;
  }, [includeDeleted, sortMode, statusFilter, t, templateFilter, templateOptions, typeFilter, typeOptions, updateQueryParam]);

  const setPageInQuery = React.useCallback((nextPage: number) => {
    const next = new URLSearchParams(searchParams);
    if (nextPage <= 1) {
      next.delete('page');
    } else {
      next.set('page', String(nextPage));
    }

    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <div>
          <h1 className={styles.heroTitle}>{t('consultations', { defaultValue: 'Consultations' })}</h1>
          <p className={styles.heroText}>
            {t('consultationsModuleIntro', {
              defaultValue: 'Browse structured consultations across assets, templates, and linked appointments. Open a record to update notes, summary, and template-driven fields.',
            })}
          </p>
        </div>
        {canManageConsultations ? (
          <div className={styles.heroActions}>
            <Link className="btn btn-secondary" to="/consultations/templates">
              {t('manageTemplates', { defaultValue: 'Manage templates' })}
            </Link>
            <Link className="btn btn-secondary" to="/consultations/types">
              {t('manageConsultationTypes', { defaultValue: 'Manage consultation types' })}
            </Link>
            <Link className="btn btn-secondary" to="/consultations/field-definitions">
              {t('manageFieldDefinitions', { defaultValue: 'Manage field definitions' })}
            </Link>
            <Link className="btn btn-primary" to="/consultations/new">
              {t('createConsultation', { defaultValue: 'Create consultation' })}
            </Link>
          </div>
        ) : null}
      </section>

      <section className={styles.toolbar}>
        <div className={styles.searchWrap}>
          <div className={styles.searchField}>
            <span className={styles.searchIcon} aria-hidden="true">
              <svg viewBox="0 0 20 20" fill="none">
                <path d="M8.75 3.5a5.25 5.25 0 1 1 0 10.5 5.25 5.25 0 0 1 0-10.5Z" stroke="currentColor" strokeWidth="1.6" />
                <path d="m12.8 12.8 3.7 3.7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            </span>
            <input
              id="consultations-search"
              type="search"
              className={styles.searchInput}
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape' && searchDraft) {
                  event.preventDefault();
                  handleClearSearch();
                }
              }}
              placeholder={t('searchConsultationsPlaceholder', {
                defaultValue: 'Search by asset, contact, template, type, status, or notes…',
              })}
              aria-label={t('searchConsultations', { defaultValue: 'Search consultations' })}
            />
            {hasActiveSearch ? (
              <button
                type="button"
                className={styles.searchClearBtn}
                onClick={handleClearSearch}
                aria-label={t('clearSearch', { defaultValue: 'Clear search' })}
              >
                <span aria-hidden="true">×</span>
              </button>
            ) : null}
          </div>
        </div>

        <div className={styles.toolbarActions}>
          <div className={styles.filterMenuWrap} ref={filterMenuRef}>
            <button
              type="button"
              className={`${styles.filterTriggerBtn} ${filtersOpen || activeFilterCount > 0 ? styles.filterTriggerBtnActive : ''}`}
              aria-label={t('filters', { defaultValue: 'Filters' })}
              aria-expanded={filtersOpen}
              onClick={() => setFiltersOpen((current) => !current)}
            >
              <span className={styles.filterTriggerLabel}>{t('filters', { defaultValue: 'Filters' })}</span>
              {activeFilterCount > 0 ? <span className={styles.filterTriggerCount}>{activeFilterCount}</span> : null}
            </button>

            {filtersOpen ? (
              <div className={styles.filterPopover}>
                <div className={styles.filterSection}>
                  <label className={styles.filterSectionTitle} htmlFor="consultations-status-filter">
                    {t('status', { defaultValue: 'Status' })}
                  </label>
                  <select id="consultations-status-filter" className="text-input" value={statusFilter} onChange={(event) => updateQueryParam('status', event.target.value)}>
                    <option value="all">{t('allStatuses', { defaultValue: 'All statuses' })}</option>
                    <option value="withStatus">{t('statusSet', { defaultValue: 'Has status' })}</option>
                    <option value="withoutStatus">{t('statusUnset', { defaultValue: 'No status' })}</option>
                  </select>
                </div>

                <div className={styles.filterSection}>
                  <label className={styles.filterSectionTitle} htmlFor="consultations-template-filter">
                    {t('template', { defaultValue: 'Template' })}
                  </label>
                  <select id="consultations-template-filter" className="text-input" value={templateFilter} onChange={(event) => updateQueryParam('template', event.target.value)}>
                    <option value="all">{t('allTemplates', { defaultValue: 'All templates' })}</option>
                    <option value="none">{t('withoutTemplate', { defaultValue: 'Without template' })}</option>
                    {templateOptions.map((option) => (
                      <option key={option.id} value={option.id}>{option.name}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.filterSection}>
                  <label className={styles.filterSectionTitle} htmlFor="consultations-type-filter">
                    {t('consultationType', { defaultValue: 'Consultation type' })}
                  </label>
                  <select id="consultations-type-filter" className="text-input" value={typeFilter} onChange={(event) => updateQueryParam('type', event.target.value)}>
                    <option value="all">{t('allConsultationTypes', { defaultValue: 'All consultation types' })}</option>
                    <option value="none">{t('withoutConsultationType', { defaultValue: 'Without consultation type' })}</option>
                    {typeOptions.map((option) => (
                      <option key={option.id} value={option.id}>{option.name}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.filterSection}>
                  <label className={styles.filterSectionTitle} htmlFor="consultations-sort-filter">
                    {t('sortBy', { defaultValue: 'Sort by' })}
                  </label>
                  <select id="consultations-sort-filter" className="text-input" value={sortMode} onChange={(event) => updateQueryParam('sort', event.target.value)}>
                    <option value="updatedDesc">{t('recentlyUpdated', { defaultValue: 'Recently updated' })}</option>
                    <option value="updatedAsc">{t('oldestUpdated', { defaultValue: 'Oldest updated' })}</option>
                    <option value="titleAsc">{t('titleAscending', { defaultValue: 'Title (A-Z)' })}</option>
                  </select>
                </div>

                <label className={styles.toggle}>
                  <input
                    type="checkbox"
                    checked={includeDeleted}
                    onChange={(event) => updateQueryParam('deleted', event.target.checked ? '1' : undefined)}
                  />
                  <span>{t('includeDeleted', { ns: 'common', defaultValue: 'Include deleted' })}</span>
                </label>

                {hasActiveFilters || hasActiveSearch ? (
                  <button className="btn btn-secondary" type="button" onClick={handleClearFilters}>
                    {t('clearFilters', { ns: 'common', defaultValue: 'Clear filters' })}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </section>

      {filterChips.length > 0 ? (
        <div className={styles.filterChips}>
          {filterChips.map((chip) => (
            <button key={chip.key} type="button" className={styles.filterChip} onClick={chip.onRemove}>
              <span>{chip.label}</span>
              <span aria-hidden="true">×</span>
            </button>
          ))}
          <button type="button" className={styles.clearChipButton} onClick={handleClearFilters}>
            {t('clearFilters', { ns: 'common', defaultValue: 'Clear filters' })}
          </button>
        </div>
      ) : null}

      {showInitialLoading ? (
        <Spinner />
      ) : filteredConsultations.length === 0 && !loading ? (
        <div className={styles.empty}>
          {t('noConsultationsFound', { defaultValue: 'No consultations matched the current filters.' })}
        </div>
      ) : (
        <>
          <div className={styles.resultsMeta}>
            <span>
              {t('consultationResultsCount', {
                defaultValue: '{{count}} consultations',
                count: totalResults,
              })}
            </span>
            {loading ? (
              <span className={styles.resultsRefreshing}>
                {t('refreshingResults', { defaultValue: 'Refreshing…' })}
              </span>
            ) : null}
          </div>

          <div className={`${styles.resultsLayout} ${loading ? styles.resultsLayoutRefreshing : ''}`}>
            <div className={styles.cardList}>
              {filteredConsultations.map((consultation) => {
                const title = getConsultationTitle(consultation);
                const isSelected = selectedConsultation?.id === consultation.id;

                return (
                  <button
                    key={consultation.id}
                    type="button"
                    className={`${styles.card} ${isSelected ? styles.cardSelected : ''}`}
                    onClick={() => setSelectedConsultationId(consultation.id)}
                  >
                    <div className={styles.cardHeader}>
                      <h2 className={styles.itemTitle}>{title}</h2>
                      <span className={styles.badge}>{consultation.status || t('draft', { defaultValue: 'Draft' })}</span>
                    </div>
                    <p className={styles.itemSubtitle}>
                      {consultation.appointmentTypeName || t('withoutConsultationType', { defaultValue: 'Without consultation type' })}
                      {' · '}
                      {consultation.templateName || t('noTemplateAssigned', { defaultValue: 'No template assigned' })}
                    </p>
                    <p className={styles.itemMeta}>
                      {formatConsultationAuditEntry(consultation, 'updated', formatDateTime, t)}
                      {' · '}
                      {consultation.assetName || consultation.customerContactName || consultation.contactName || t('noLinkedContact', { defaultValue: 'No linked contact' })}
                    </p>
                    <p className={styles.itemSummary}>
                      {consultation.summary || consultation.notes || t('consultationNoSummary', { defaultValue: 'Open this record to add notes, summary, and structured consultation data.' })}
                    </p>
                    {consultation.createdByAi ? (
                      <span className={styles.aiBadge}>
                        {consultation.aiStatus === 'approved'
                          ? t('aiApproved', { defaultValue: 'AI approved' })
                          : consultation.aiStatus === 'rejected'
                            ? t('aiRejected', { defaultValue: 'AI rejected' })
                            : t('aiPendingApproval', { defaultValue: 'AI draft - pending approval' })}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>

            {selectedConsultation ? (
              <aside className={styles.previewPane}>
                <div className={styles.previewHeader}>
                  <h3>{getConsultationTitle(selectedConsultation)}</h3>
                  <span className={styles.badge}>{selectedConsultation.status || t('draft', { defaultValue: 'Draft' })}</span>
                </div>
                <dl className={styles.previewMeta}>
                  <div>
                    <dt>{t('consultationType', { defaultValue: 'Consultation type' })}</dt>
                    <dd>{selectedConsultation.appointmentTypeName || '-'}</dd>
                  </div>
                  <div>
                    <dt>{t('template', { defaultValue: 'Template' })}</dt>
                    <dd>{selectedConsultation.templateName || t('noTemplateAssigned', { defaultValue: 'No template assigned' })}</dd>
                  </div>
                  <div>
                    <dt>{t('asset', { ns: 'assets', defaultValue: 'Asset' })}</dt>
                    <dd>{selectedConsultation.assetName || '-'}</dd>
                  </div>
                  <div>
                    <dt>{t('contact', { defaultValue: 'Contact' })}</dt>
                    <dd>{selectedConsultation.customerContactName || selectedConsultation.contactName || '-'}</dd>
                  </div>
                  <div>
                    <dt>{t('created', { ns: 'common', defaultValue: 'Created' })}</dt>
                    <dd>{formatConsultationAuditEntry(selectedConsultation, 'created', formatDateTime, t)}</dd>
                  </div>
                  <div>
                    <dt>{t('updated', { ns: 'common', defaultValue: 'Updated' })}</dt>
                    <dd>{formatConsultationAuditEntry(selectedConsultation, 'updated', formatDateTime, t)}</dd>
                  </div>
                </dl>
                <p className={styles.previewSummary}>
                  {selectedConsultation.summary || selectedConsultation.notes || t('consultationNoSummary', { defaultValue: 'Open this record to add notes, summary, and structured consultation data.' })}
                </p>
                <Link className="btn btn-primary" to={`/consultations/${selectedConsultation.id}${location.search}`}>
                  {t('openConsultation', { defaultValue: 'Open consultation' })}
                </Link>
              </aside>
            ) : null}
          </div>

          <div className={styles.pagination}>
            <button className="btn btn-secondary" type="button" onClick={() => setPageInQuery(page - 1)} disabled={page <= 1}>
              {t('previous', { ns: 'common', defaultValue: 'Previous' })}
            </button>
            <span className={styles.pageMeta}>{t('pageCount', { defaultValue: 'Page {{page}}', page })}</span>
            <button className="btn btn-secondary" type="button" onClick={() => setPageInQuery(page + 1)} disabled={!hasNextPage}>
              {t('next', { ns: 'common', defaultValue: 'Next' })}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ConsultationsPage;
